import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Box, Breadcrumbs, Typography, Stack, Button, TextField, Alert, CircularProgress, Paper } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getCompanySettings, updateCompanySettings, type SaveCompanySettingsPayload } from '../../api/financiar'
import { errorMessage } from '../../api/client'

const schema = z.object({
  name: z.string().min(1, 'Câmp obligatoriu'),
  cui: z.string().nullable(),
  regCom: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  country: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  bankNameRON: z.string().nullable(),
  ibanRON: z.string().nullable(),
  bankNameEUR: z.string().nullable(),
  ibanEUR: z.string().nullable(),
}) satisfies z.ZodType<SaveCompanySettingsPayload>
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  name: '', cui: '', regCom: '', address: '', city: '', county: '', country: '', phone: '', email: '',
  bankNameRON: '', ibanRON: '', bankNameEUR: '', ibanEUR: '',
}

export default function CompanySettingsPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['financiar', 'setari'], queryFn: getCompanySettings })
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ defaultValues: EMPTY, resolver: zodResolver(schema) })

  useEffect(() => {
    if (data) reset({ ...data })
  }, [data, reset])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => updateCompanySettings(values),
    onSuccess: (r) => { qc.setQueryData(['financiar', 'setari'], r) },
  })

  if (isLoading) {
    return <Stack sx={{ py: 6, alignItems: 'center' }}><CircularProgress /></Stack>
  }

  const field = (name: keyof FormValues, label: string, width?: number) => (
    <Controller name={name} control={control} render={({ field: f }) => (
      <TextField {...f} value={f.value ?? ''} label={label} sx={width ? { width } : { flexGrow: 1 }}
        error={!!errors[name]} helperText={errors[name]?.message} />
    )} />
  )

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Configurare</Typography>
        <Typography variant="inherit" color="text.primary">Profil firmă</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Profil firmă</Typography>
      <Typography variant="body2" color="text.secondary">
        Datele emitentului, tipărite pe antetul facturilor (FR-FFC-001).
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              {field('name', 'Denumire')}
              {field('cui', 'CUI', 160)}
              {field('regCom', 'Reg. Com.', 200)}
            </Stack>
            <Stack direction="row" spacing={2}>
              {field('address', 'Adresă')}
              {field('city', 'Localitate', 180)}
              {field('county', 'Județ', 160)}
              {field('country', 'Țară', 140)}
            </Stack>
            <Stack direction="row" spacing={2}>
              {field('phone', 'Telefon', 200)}
              {field('email', 'Email')}
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Conturi bancare</Typography>
            <Stack direction="row" spacing={2}>
              {field('bankNameRON', 'Bancă (RON)')}
              {field('ibanRON', 'IBAN RON')}
            </Stack>
            <Stack direction="row" spacing={2}>
              {field('bankNameEUR', 'Bancă (EUR)')}
              {field('ibanEUR', 'IBAN EUR')}
            </Stack>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isSuccess && <Alert severity="success">Salvat.</Alert>}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={mutation.isPending}
                startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
                Salvează
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Stack>
  )
}
